import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import type { OpMeta } from '@/catalog/types'
import { SearchInput } from '@/components/data/SearchInput'
import { useOpPrefs } from '@/lib/opPrefs'
import { searchOps } from '@/lib/opSearch'

const MATCH_CAP = 50
const domainOf = (id: string) => id.split('.')[0]

const headingClass = 'mb-1 mt-3 px-2 text-[11px] font-medium uppercase tracking-[0.07em] text-text-3 first:mt-0'

function Entry({ id, current, label }: { id: string; current: boolean; label?: string }) {
  return (
    <li>
      <Link
        to={`/operations/${id}`}
        aria-current={current ? 'page' : undefined}
        title={id}
        className={[
          'block truncate rounded-[5px] px-2 py-[5px] font-mono text-[12px] transition-colors',
          current ? 'bg-surface-selected text-foreground' : 'text-text-2 hover:bg-surface-raised hover:text-foreground',
        ].join(' ')}
      >
        {label ?? id}
      </Link>
    </li>
  )
}

/**
 * Left column of the operation page: search the catalog (same ranking as Operations and ⌘K),
 * the current operation's domain and Recent — to move between operations without the list.
 */
export function OpCatalogRail({ ops, currentId, onHide }: {
  ops: OpMeta[]
  currentId: string
  onHide: () => void
}) {
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const { recent } = useOpPrefs()
  const query = q.trim()
  const domain = domainOf(currentId)

  const matches = useMemo(() => (query ? searchOps(ops, query).slice(0, MATCH_CAP) : []), [ops, query])
  const inDomain = useMemo(
    () => ops.filter((o) => domainOf(o.operation_id) === domain)
      .sort((a, b) => a.operation_id.localeCompare(b.operation_id)),
    [ops, domain],
  )
  const known = useMemo(() => new Set(ops.map((o) => o.operation_id)), [ops])
  const recentIds = recent.filter((id) => known.has(id))

  return (
    <nav aria-label="Operation catalog" className="flex w-[240px] shrink-0 flex-col border-r border-line bg-pane">
      <div className="flex shrink-0 items-center gap-1.5 px-3 pb-2 pt-3">
        <SearchInput
          value={q}
          onChange={setQ}
          label="Search operations"
          placeholder="Search operations"
          width="w-full"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && matches[0]) {
              e.preventDefault()
              navigate(`/operations/${matches[0].operation_id}`)
            }
          }}
        />
        <button
          type="button"
          onClick={onHide}
          aria-label="Hide catalog"
          aria-expanded="true"
          title="Hide catalog"
          className="rounded-md p-1.5 text-text-3 transition-colors hover:bg-surface-raised hover:text-foreground"
        >
          <PanelLeftClose aria-hidden className="size-4" />
        </button>
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        {query ? (
          <>
            <h2 className={headingClass}>
              Matches <span className="font-mono normal-case tracking-normal">{matches.length}</span>
            </h2>
            {matches.length === 0
              ? <p className="px-2 py-1 text-[12px] text-text-3">No operations match.</p>
              : (
                <ul aria-label="Matching operations">
                  {matches.map((o) => <Entry key={o.operation_id} id={o.operation_id} current={o.operation_id === currentId} />)}
                </ul>
              )}
          </>
        ) : (
          <>
            <h2 className={headingClass}>{domain}</h2>
            <ul aria-label={`${domain} operations`}>
              {inDomain.map((o) => (
                <Entry
                  key={o.operation_id}
                  id={o.operation_id}
                  label={o.operation_id.slice(domain.length + 1)}
                  current={o.operation_id === currentId}
                />
              ))}
            </ul>
            {recentIds.length > 0 && (
              <>
                <h2 className={headingClass}>Recent</h2>
                <ul aria-label="Recent operations">
                  {recentIds.map((id) => <Entry key={id} id={id} current={id === currentId} />)}
                </ul>
              </>
            )}
          </>
        )}
      </div>
    </nav>
  )
}

/** The rail folded away: one button that brings it back. */
export function OpCatalogRailCollapsed({ onShow }: { onShow: () => void }) {
  return (
    <div className="flex w-10 shrink-0 flex-col items-center border-r border-line bg-pane pt-3">
      <button
        type="button"
        onClick={onShow}
        aria-label="Show catalog"
        aria-expanded="false"
        title="Show catalog"
        className="rounded-md p-1.5 text-text-3 transition-colors hover:bg-surface-raised hover:text-foreground"
      >
        <PanelLeftOpen aria-hidden className="size-4" />
      </button>
    </div>
  )
}
